import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { userService } from '../services/userService';
import { Users, LogOut, Plus, Search, Edit, Trash2, X, Eye } from 'lucide-react';
import Layout from '../components/Layout';
import MemberForm from '../components/MemberForm';

const Dashboard = () => {
  const { user, logout } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [showMemberForm, setShowMemberForm] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberDetails, setMemberDetails] = useState(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await userService.getUsers();
      // Filtrar solo usuarios con rol de socio
      const memberUsers = (data.data || []).filter(user => user.role?.name === 'member');
      setUsers(memberUsers);
    } catch (error) {
      console.error('Error al cargar usuarios:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  const getRoleBadge = (role) => {
    if (!role) return null;
    
    const badges = {
      super_admin: { text: 'Super Admin', color: 'bg-red-100 text-red-800' },
      club: { text: 'Club', color: 'bg-blue-100 text-blue-800' },
      member: { text: 'Socio', color: 'bg-green-100 text-green-800' },
    };
    
    const badge = badges[role.name] || { text: role.display_name || role.name, color: 'bg-gray-100 text-gray-800' };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
        {badge.text}
      </span>
    );
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    let matchesFilter = true;
    if (filterRole === 'active') {
      matchesFilter = user.active === true;
    } else if (filterRole === 'inactive') {
      matchesFilter = user.active === false;
    }
    
    return matchesSearch && matchesFilter;
  });

  return (
    <Layout title="Socios" description="Gestiona los socios de tu club">
          {/* Filters and search */}
          <div className="mb-6 flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar usuarios..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
            <div className="sm:w-48">
              <select
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Todos los socios</option>
                <option value="active">Solo activos</option>
                <option value="inactive">Solo inactivos</option>
              </select>
            </div>
            <button 
              onClick={() => {
                setEditingMember(null);
                setShowMemberForm(true);
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nuevo Socio
            </button>
          </div>

          {/* Users table */}
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Usuario
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Rol
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Club
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Estado
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredUsers.map((user) => (
                      <tr 
                        key={user.id} 
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={async () => {
                          setSelectedMember(user);
                          setShowDetailModal(true);
                          try {
                            const details = await userService.getUser(user.id);
                            setMemberDetails(details);
                          } catch (error) {
                            console.error('Error al cargar detalles:', error);
                          }
                        }}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                                <span className="text-sm font-medium text-primary-600">
                                  {user.name.charAt(0).toUpperCase()}
                                </span>
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {user.name} {user.surname || ''}
                              </div>
                              <div className="text-sm text-gray-500">
                                {user.parent_email ? user.parent_email : user.email}
                                {user.parent_email && <span className="text-xs text-gray-400 ml-1">(Hijo)</span>}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getRoleBadge(user.role)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {user.club?.name || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            user.active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {user.active ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2" onClick={(e) => e.stopPropagation()}>
                            <button 
                              onClick={() => {
                                setEditingMember(user);
                                setShowMemberForm(true);
                              }}
                              className="text-primary-600 hover:text-primary-900"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button 
                              onClick={() => {
                                setMemberToDelete(user);
                                setShowDeleteModal(true);
                              }}
                              className="text-red-600 hover:text-red-900"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            {!loading && filteredUsers.length === 0 && (
              <div className="text-center py-12">
                <Users className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No hay usuarios</h3>
                <p className="mt-1 text-sm text-gray-500">
                  No se encontraron usuarios con los filtros aplicados.
                </p>
              </div>
            )}
          </div>

      {/* Modal del formulario de socio */}
      <MemberForm
        isOpen={showMemberForm}
        onClose={() => {
          setShowMemberForm(false);
          setEditingMember(null);
        }}
        onSuccess={async (result) => {
          await loadUsers();
          setShowMemberForm(false);
          setEditingMember(null);
        }}
        memberData={editingMember}
        isEditing={!!editingMember}
      />

      {/* Modal de confirmación de eliminación */}
      {showDeleteModal && memberToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirmar eliminación</h3>
            <p className="text-gray-600 mb-6">
              ¿Estás seguro de que deseas eliminar al socio <strong>{memberToDelete.name}</strong>? Esta acción no se puede deshacer.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setMemberToDelete(null);
                }}
                disabled={deleting}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={async () => {
                  if (!memberToDelete) return;
                  setDeleting(true);
                  try {
                    await userService.deleteUser(memberToDelete.id);
                    await loadUsers();
                    setShowDeleteModal(false);
                    setMemberToDelete(null);
                  } catch (error) {
                    console.error('Error al eliminar socio:', error);
                    alert('Error al eliminar el socio. Inténtalo de nuevo.');
                  } finally {
                    setDeleting(false);
                  }
                }}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {deleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Eliminando...
                  </>
                ) : (
                  'Eliminar'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de detalles del socio */}
      {showDetailModal && selectedMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">Detalles del Socio</h3>
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedMember(null);
                  setMemberDetails(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6">
              {memberDetails ? (
                    <div className="space-y-6">
                  {/* Información del socio principal */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">
                      {memberDetails.is_child ? 'Información del Hijo' : 'Información del Socio'}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Nombre</label>
                        <p className="mt-1 text-sm text-gray-900">{memberDetails.name} {memberDetails.surname || ''}</p>
                      </div>
                      {!memberDetails.is_child && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Email</label>
                          <p className="mt-1 text-sm text-gray-900">{memberDetails.email}</p>
                        </div>
                      )}
                      {memberDetails.phone && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Teléfono</label>
                          <p className="mt-1 text-sm text-gray-900">{memberDetails.phone}</p>
                        </div>
                      )}
                      {memberDetails.birth_date && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Fecha de Nacimiento</label>
                          <p className="mt-1 text-sm text-gray-900">{new Date(memberDetails.birth_date).toLocaleDateString('es-ES')}</p>
                        </div>
                      )}
                      {memberDetails.shirt_size && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Talla de Camiseta</label>
                          <p className="mt-1 text-sm text-gray-900">{memberDetails.shirt_size.toUpperCase()}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Información del padre si es hijo */}
                  {memberDetails.is_child && memberDetails.parent && (
                    <div className="border-t border-gray-200 pt-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Información del Padre</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Nombre</label>
                          <p className="mt-1 text-sm text-gray-900">{memberDetails.parent.name} {memberDetails.parent.surname || ''}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Email</label>
                          <p className="mt-1 text-sm text-gray-900">{memberDetails.parent.email}</p>
                        </div>
                        {memberDetails.parent.phone && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Teléfono</label>
                            <p className="mt-1 text-sm text-gray-900">{memberDetails.parent.phone}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Información de hijos si es padre */}
                  {!memberDetails.is_child && memberDetails.child_users && memberDetails.child_users.length > 0 && (
                    <div className="border-t border-gray-200 pt-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Hijos ({memberDetails.child_users.length})</h4>
                      <div className="space-y-4">
                        {memberDetails.child_users.map((child, index) => (
                          <div key={child.id || index} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700">Nombre</label>
                                <p className="mt-1 text-sm text-gray-900">{child.name} {child.surname || ''}</p>
                              </div>
                              {child.birth_date && (
                                <div>
                                  <label className="block text-sm font-medium text-gray-700">Fecha de Nacimiento</label>
                                  <p className="mt-1 text-sm text-gray-900">{new Date(child.birth_date).toLocaleDateString('es-ES')}</p>
                                </div>
                              )}
                              {child.shirt_size && (
                                <div>
                                  <label className="block text-sm font-medium text-gray-700">Talla de Camiseta</label>
                                  <p className="mt-1 text-sm text-gray-900">{child.shirt_size.toUpperCase()}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Cargando detalles...</p>
                </div>
              )}
            </div>

            <div className="flex justify-end p-6 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedMember(null);
                  setMemberDetails(null);
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Dashboard;
